import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { cn } from "@/lib/utils";
import { Cookie } from "lucide-react";
import type { CookieCategory } from "@/lib/cookieConsent";

function CookieToggles({
  analytics,
  personalization,
  updateCategory,
}: {
  analytics: boolean;
  personalization: boolean;
  updateCategory: (category: CookieCategory, value: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm text-muted-foreground">{t("cookie.advertising")}</Label>
        <Switch checked={false} disabled aria-label={t("cookie.advertising")} />
      </div>
      <div className="flex items-center justify-between">
        <Label htmlFor="cookie-personalization" className="text-sm text-foreground">
          {t("cookie.personalization")}
        </Label>
        <Switch
          id="cookie-personalization"
          checked={personalization}
          onCheckedChange={(v) => updateCategory("personalization", v)}
          aria-label={t("cookie.personalization")}
        />
      </div>
      <div className="flex items-center justify-between">
        <Label htmlFor="cookie-analytics" className="text-sm text-foreground">
          {t("cookie.analytics")}
        </Label>
        <Switch
          id="cookie-analytics"
          checked={analytics}
          onCheckedChange={(v) => updateCategory("analytics", v)}
          aria-label={t("cookie.analytics")}
        />
      </div>
    </div>
  );
}

function CookieButtons({
  save,
  acceptAll,
  rejectNonEssential,
}: {
  save: () => void;
  acceptAll: () => void;
  rejectNonEssential: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={save}>
          {t("cookie.save")}
        </Button>
        <Button size="sm" className="flex-1" onClick={acceptAll}>
          {t("cookie.acceptAll")}
        </Button>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        onClick={rejectNonEssential}
      >
        {t("cookie.rejectNonEssential")}
      </Button>
    </>
  );
}

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const {
    isVisible,
    analytics,
    personalization,
    save,
    acceptAll,
    rejectNonEssential,
    updateCategory,
  } = useCookieConsent();

  if (!isVisible) return null;

  const togglesProps = { analytics, personalization, updateCategory };
  const buttonsProps = { save, acceptAll, rejectNonEssential };

  // TES-1728 (N4): Non-modal Consent-Banner als fixed Card statt Mobile-Drawer.
  // Der Drawer (vaul) legte als modaler Bottom-Sheet eine Overlay-Ebene (z-50)
  // ÜBER die mobile BottomNav (z-50) und verdeckte den „MÄNGEL"-Tab. Stattdessen
  // sitzt der Banner jetzt in EINER Variante über dem Inhalt, aber klar UNTERHALB
  // der BottomNav: Mobile-Bottom-Anker = BottomNav-Höhe (h-16 = 4rem) + safe-area,
  // Desktop floatet er unten rechts und bleibt frei vom Table-/Selection-Footer.
  // z-40 = unter der Shell (TopAppBar/BottomNav/SideNav = z-50), damit die
  // Navigation sichtbar/bedienbar bleibt; das Banner liegt nur über dem Content.
  return (
    <div
      data-testid="cookie-consent-banner"
      className={cn(
        "fixed inset-x-0 z-40 p-4",
        // Mobile: Bottom-Anker = BottomNav-Höhe (h-16 = 4rem) + Geräte-Safe-Area,
        // damit der Banner vollständig ÜBER der BottomNav endet (MÄNGEL-Tab frei).
        "[bottom:calc(4rem+env(safe-area-inset-bottom))]",
        // Desktop: keine BottomNav → unten rechts andocken, schmaler Karten-Footprint
        // (DS-Scale-Token bottom-4 = 1rem, symmetrisch zu right-4; nur Mobile braucht
        // wegen env(safe-area-inset-bottom) den arbitrary-calc-Wert).
        "md:inset-x-auto md:bottom-4 md:left-auto md:right-4 md:max-w-md md:p-0",
      )}
    >
      <Card className="shadow-lg border-border">
        <CardContent className="space-y-4 p-5">
          <div className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold text-foreground">{t("cookie.title")}</h3>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("cookie.description")}{" "}
            <Link to="/privacy" className="underline text-primary hover:text-primary/80">
              {t("cookie.privacyLink")}
            </Link>
          </p>
          <Separator />
          <CookieToggles {...togglesProps} />
          <Separator />
          <CookieButtons {...buttonsProps} />
        </CardContent>
      </Card>
    </div>
  );
}
